import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, ChevronRight, Check, Crown, Star, Lock, Zap, RefreshCw, AlertTriangle, Tag, X as XIcon } from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import StripeCardInput from '../components/StripeCardInput';
import { formatTime12Hour } from '../utils/timeFormat';

const Booking = () => {
  // Load Stripe inside component to avoid breaking other pages if key is missing
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (key && key !== 'undefined') {
      setStripePromise(loadStripe(key));
    }
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [selectedService, setSelectedService] = useState(location.state?.selectedService || null);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [notes, setNotes] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringInterval, setRecurringInterval] = useState('weekly');
  const [recurringOccurrences, setRecurringOccurrences] = useState(4); // For display only
  const [recurringFrequency, setRecurringFrequency] = useState('weekly'); // Alias for recurringInterval
  const [policyAgreed, setPolicyAgreed] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null); // {title, discount_type, discount_value}
  const [promoChecking, setPromoChecking] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState(null);
  const [cardSaved, setCardSaved] = useState(false);
  const [hasCardOnFile, setHasCardOnFile] = useState(false);

  // Check auth
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  // Fetch services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await api.get('/api/services');
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
        toast.error('Failed to load services');
      }
    };
    fetchServices();
  }, []);

  // Pre-fill promo code from ?promo= query param (e.g. from the login popup CTA)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('promo');
    if (code) {
      setPromoCode(code.trim().toUpperCase());
      toast.success(`Promo code ${code.toUpperCase()} added — apply it after picking your date`);
    }
  }, [location.search]);

  // Fetch barbers
  useEffect(() => {
    const fetchBarbers = async () => {
      try {
        const response = await api.get('/api/barbers');
        setBarbers(response.data);
      } catch (error) {
        console.error('Error fetching barbers:', error);
        toast.error('Failed to load barbers');
      }
    };
    fetchBarbers();
  }, []);

  // Fetch shop holidays for calendar markers + selection messaging
  const [holidays, setHolidays] = useState([]);
  const [openOverrides, setOpenOverrides] = useState([]);
  useEffect(() => {
    const fetchCalendarData = async () => {
      try {
        const [hRes, oRes] = await Promise.all([
          api.get('/api/holidays'),
          api.get('/api/open-overrides'),
        ]);
        setHolidays(Array.isArray(hRes.data) ? hRes.data : []);
        setOpenOverrides(Array.isArray(oRes.data) ? oRes.data : []);
      } catch (error) {
        console.error('Error fetching calendar data:', error);
      }
    };
    fetchCalendarData();
  }, []);

  // Set of YYYY-MM-DD strings for dates the admin has flagged as "open this Monday/Sunday"
  const openOverrideSet = React.useMemo(
    () => new Set(openOverrides.map((o) => o.date)),
    [openOverrides]
  );

  // Build helper maps for the calendar
  const parseLocalDate = (yyyyMmDd) => {
    const [y, m, d] = yyyyMmDd.split('-').map(Number);
    return new Date(y, m - 1, d);
  };
  const activeHolidays = holidays.filter(h => h.active !== false);
  const closedHolidayDates = activeHolidays
    .filter(h => h.closed)
    .map(h => parseLocalDate(h.date));
  const limitedHolidayDates = activeHolidays
    .filter(h => !h.closed && (h.early_close_time || h.open_time || h.members_only))
    .map(h => parseLocalDate(h.date));
  const holidayByDateStr = activeHolidays.reduce((acc, h) => {
    acc[h.date] = h;
    return acc;
  }, {});

  const selectedDateStr = selectedDate
    ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
    : null;
  const selectedHoliday = selectedDateStr ? holidayByDateStr[selectedDateStr] : null;

  // Check if user has card on file
  useEffect(() => {
    const checkCardOnFile = async () => {
      try {
        console.log('[Booking] Checking card on file status...');
        const response = await api.get('/api/has-card-on-file');
        console.log('[Booking] Card on file response:', response.data);
        setHasCardOnFile(response.data.has_card_on_file);
        console.log('[Booking] hasCardOnFile state set to:', response.data.has_card_on_file);
      } catch (error) {
        console.error('Error checking card on file:', error);
        setHasCardOnFile(false);
      }
    };
    if (currentUser) {
      checkCardOnFile();
    }
  }, [currentUser]);

  // Create SetupIntent when reaching Step 4 and no card on file
  useEffect(() => {
    const createSetupIntent = async () => {
      if (step === 4 && !hasCardOnFile && !setupIntentClientSecret) {
        try {
          const response = await api.post('/api/create-setup-intent');
          setSetupIntentClientSecret(response.data.client_secret);
        } catch (error) {
          console.error('Error creating setup intent:', error);
          toast.error('Failed to initialize card setup');
        }
      }
    };
    createSetupIntent();
  }, [step, hasCardOnFile, setupIntentClientSecret]);

  const [availabilityReason, setAvailabilityReason] = useState('');
  // Fetch availability when barber and date are selected
  useEffect(() => {
    const loadAvailability = async () => {
      if (selectedBarber && selectedDate) {
        setLoadingAvailability(true);
        try {
          // Build local YYYY-MM-DD (avoid toISOString → UTC shift bug)
          const y = selectedDate.getFullYear();
          const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
          const d = String(selectedDate.getDate()).padStart(2, '0');
          const dateStr = `${y}-${m}-${d}`;
          const response = await api.get(`/api/availability/${selectedBarber.id}?date=${dateStr}`);
          setAvailableSlots(response.data.available_slots);
          setAvailabilityReason(response.data.reason || '');
        } catch (error) {
          console.error('Error fetching availability:', error);
          toast.error('Failed to load availability');
          setAvailableSlots([]);
          setAvailabilityReason('');
        } finally {
          setLoadingAvailability(false);
        }
      }
    };
    loadAvailability();
  }, [selectedBarber, selectedDate]);

  const hasMembership = currentUser?.membership_tier && currentUser.membership_tier !== 'none';
  const depositRequired = currentUser?.requires_deposit || false;

  const getMembershipBadge = () => {
    if (!hasMembership) return null;
    
    if (currentUser.membership_tier === 'vip') {
      return (
        <Badge className="bg-gradient-to-r from-lime-500 to-lime-600 text-black border-purple-500">
          <Crown className="w-3 h-3 mr-1" />
          VIP Member
        </Badge>
      );
    }
    
    return (
      <Badge className="bg-purple-600 text-lime-400">
        <Star className="w-3 h-3 mr-1" />
        Sauce Member
      </Badge>
    );
  };

  const handleNext = () => {
    if (step === 1 && !selectedService) {
      toast.error('Please select a service');
      return;
    }
    if (step === 2 && !selectedBarber) {
      toast.error('Please select a barber');
      return;
    }
    if (step === 3 && (!selectedDate || !selectedTime)) {
      toast.error('Please select date and time');
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleCardSaved = async (setupIntentId, paymentMethodId) => {
    try {
      await api.post('/api/confirm-card-saved', {
        setup_intent_id: setupIntentId,
        payment_method_id: paymentMethodId
      });
      setCardSaved(true);
      setHasCardOnFile(true);
      toast.success('Card saved successfully!');
    } catch (error) {
      console.error('Error confirming card saved:', error);
      toast.error('Failed to save card');
    }
  };

  const applyPromoCode = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    if (!selectedDate) {
      setPromoError('Pick a date first so we can check the promo applies');
      return;
    }
    setPromoChecking(true);
    setPromoError('');
    try {
      const y = selectedDate.getFullYear();
      const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const d = String(selectedDate.getDate()).padStart(2, '0');
      const r = await api.post('/api/promotions/validate', {
        promo_code: code,
        appointment_date: `${y}-${m}-${d}`,
      });
      setPromoApplied(r.data);
      toast.success(`Code ${code} applied 🎉`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Invalid promo code';
      setPromoError(msg);
      setPromoApplied(null);
    } finally {
      setPromoChecking(false);
    }
  };

  const clearPromoCode = () => {
    setPromoCode('');
    setPromoApplied(null);
    setPromoError('');
  };

  const computedFinalPrice = (() => {
    if (!selectedService) return null;
    const base = parseFloat(selectedService.price) || 0;
    if (!promoApplied) return base;
    if (promoApplied.discount_type === 'percentage') {
      return Math.max(0, +(base * (1 - parseFloat(promoApplied.discount_value) / 100)).toFixed(2));
    }
    return Math.max(0, +(base - parseFloat(promoApplied.discount_value)).toFixed(2));
  })();

  const handleBooking = async () => {
    if (!policyAgreed) {
      toast.error('Please agree to the no-show policy to continue');
      return;
    }

    if (!smsConsent) {
      toast.error('Please agree to receive SMS notifications to continue');
      return;
    }

    if (!hasCardOnFile && !cardSaved) {
      toast.error('Please save your card before booking');
      return;
    }
    
    // **NEW: Validate user has name before booking**
    if (!currentUser?.name || currentUser.name.trim() === '' || currentUser.name.toLowerCase() === 'unknown') {
      toast.error('⚠️ Name Required: Please complete your profile before booking');
      setTimeout(() => navigate('/profile'), 2000);
      return;
    }

    // **NEW: Validate user has phone before booking**
    if (!currentUser?.phone || currentUser.phone.trim() === '') {
      toast.error('⚠️ Phone Required: Please add your phone number in your profile');
      setTimeout(() => navigate('/profile'), 2000);
      return;
    }
    
    if (currentUser?.requires_deposit) {
      toast.error('Deposit required. Please contact shop at (239) 201-8105');
      return;
    }
    
    setLoading(true);
    
    try {
      // Build local YYYY-MM-DD to avoid timezone off-by-one
      const y = selectedDate.getFullYear();
      const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const d = String(selectedDate.getDate()).padStart(2, '0');
      const isoDate = `${y}-${m}-${d}`;

      // Create appointment via API
      const response = await api.post('/api/appointments', {
        barber_id: selectedBarber.id,
        service_id: selectedService.id,
        date: isoDate,
        time: selectedTime,
        notes: notes,
        recurring: isRecurring,
        recurring_interval: isRecurring ? recurringInterval : null,
        promo_code: promoApplied ? promoCode.trim().toUpperCase() : null,
        sms_consent: smsConsent,
        sms_consent_at: smsConsent ? new Date().toISOString() : null,
      });
      
      toast.success('Appointment booked successfully! 🎉', {
        description: `${selectedService.name} with ${selectedBarber.name}`,
        duration: 4000
      });
      
      // TODO: Send SMS notification (will integrate later)
      
      navigate('/my-appointments');
    } catch (error) {
      console.error('Booking error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to book appointment';
      
      // **NEW: Handle specific validation errors**
      if (errorMsg.includes('name') || errorMsg.includes('profile')) {
        toast.error(errorMsg);
        setTimeout(() => navigate('/profile'), 2000);
      } else if (errorMsg.includes('phone')) {
        toast.error(errorMsg);
        setTimeout(() => navigate('/profile'), 2000);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate today at midnight (allow same-day bookings)
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to midnight

  return (
    <div className="sauce-sity-page" data-testid="booking-page">
      <div className="sauce-sity-content" style={{ maxWidth: 1100 }}>
        <div className="ss-section-header ss-mb-48">
          <h1 className="ss-title-xl">Book Your <span className="ss-highlight-green">Appointment</span></h1>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 24, flexWrap: 'wrap' }}>
            {[1, 2, 3, 4].map(num => (
              <div key={num} style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: 40, height: 40,
                  borderRadius: '999px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800,
                  background: step >= num ? 'var(--ss-green)' : 'rgba(20,20,20,0.88)',
                  color: step >= num ? '#111' : 'var(--ss-white-soft)',
                  border: step >= num ? 'none' : '1px solid var(--ss-border)',
                }}>
                  {step > num ? <Check className="w-5 h-5" /> : num}
                </div>
                {num < 4 && (
                  <div style={{
                    width: 48, height: 3,
                    background: step > num ? 'var(--ss-green)' : 'rgba(20,20,20,0.88)',
                  }}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: Select Service */}
        {step === 1 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Select a Service</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map(service => (
                <Card 
                  key={service.id}
                  className={`cursor-pointer transition-all ${
                    selectedService?.id === service.id 
                      ? 'bg-lime-500/10 border-lime-500' 
                      : 'bg-zinc-900 border-zinc-800 hover:border-lime-500/60'
                  }`}
                  onClick={() => setSelectedService(service)}
                >
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-2">{service.name}</h3>
                    <p className="text-zinc-400 text-sm mb-4">{service.description}</p>
                    <div className="flex justify-end items-center">
                      <span className="text-zinc-500 text-sm">{service.duration} min</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Select Barber */}
        {step === 2 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Choose Your Barber</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {barbers.map(barber => (
                <Card 
                  key={barber.id}
                  className={`cursor-pointer transition-all ${
                    selectedBarber?.id === barber.id 
                      ? 'bg-lime-500/10 border-lime-500' 
                      : 'bg-zinc-900 border-zinc-800 hover:border-lime-500/60'
                  }`}
                  onClick={() => setSelectedBarber(barber)}
                >
                  {barber.avatar ? (
                    <img 
                      src={barber.avatar} 
                      alt={barber.name}
                      className="w-full h-64 object-cover"
                    />
                  ) : (
                    <div className="w-full h-64 bg-zinc-800 flex items-center justify-center">
                      <p className="text-zinc-600 text-sm">Add barber photo</p>
                    </div>
                  )}
                  <CardContent className="p-4">
                    <h3 className="text-lg font-semibold text-white mb-1">{barber.name}</h3>
                    <p className="text-lime-400 text-sm mb-2">{barber.specialty}</p>
                    <div className="flex items-center justify-between text-sm text-zinc-400">
                      <span>{barber.experience}</span>
                      <span>⭐ {barber.rating}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Select Date & Time */}
        {step === 3 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Select Date & Time</h2>
              {getMembershipBadge()}
            </div>

            {/* Priority Access Banner for Members */}
            {hasMembership && (
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 border border-lime-500 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <Zap className="w-6 h-6 text-lime-400 mr-3" />
                  <div>
                    <h3 className="font-semibold text-lime-400">Priority Access Active</h3>
                    <p className="text-sm text-white">✓ Exclusive member-only time slots</p>
                    <p className="text-sm text-white">✓ Book holiday appointments 2 months early (non-members wait 2 weeks)</p>
                  </div>
                </div>
              </div>
            )}

            {/* Upgrade Prompt for Non-Members */}
            {!hasMembership && (
              <div className="bg-zinc-900 border border-purple-500 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Crown className="w-6 h-6 text-purple-400 mr-3" />
                    <div>
                      <h3 className="font-semibold text-purple-400">Unlock Priority Booking</h3>
                      <p className="text-sm text-zinc-400">• Exclusive early morning & peak hour slots</p>
                      <p className="text-sm text-zinc-400">• Book holiday appointments 2 months early (vs. 2 weeks)</p>
                    </div>
                  </div>
                  <Link to="/membership">
                    <Button className="bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700">
                      Upgrade
                    </Button>
                  </Link>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold mb-4">Choose Date</h3>
                <div className="bg-zinc-900 p-4 rounded-lg">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => {
                      const day = date.getDay(); // 0 = Sunday, 1 = Monday
                      const ds = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                      const h = holidayByDateStr[ds];
                      // A holiday with closed=false (limited hours) OR an explicit
                      // open-override both count as "shop is working this day".
                      const isOpenedByAdmin = openOverrideSet.has(ds) || (h && h.closed === false);

                      // Block Sundays (0) and Mondays (1) UNLESS admin flagged this specific date open
                      if ((day === 0 || day === 1) && !isOpenedByAdmin) {
                        return true;
                      }

                      // Disable past dates (but allow today for same-day bookings)
                      if (date < today) return true;

                      // Disable closed-holiday dates
                      if (h && h.closed) return true;
                      return false;
                    }}
                    modifiers={{
                      holidayClosed: closedHolidayDates,
                      holidayLimited: limitedHolidayDates,
                      openOverride: openOverrides.map((o) => parseLocalDate(o.date)),
                    }}
                    modifiersClassNames={{
                      holidayClosed: 'bg-red-900/40 text-red-300 line-through relative',
                      holidayLimited: 'bg-amber-900/40 text-amber-300 ring-1 ring-amber-500/40 relative',
                      openOverride: 'bg-lime-900/40 text-lime-300 ring-1 ring-lime-500/50 relative font-semibold',
                    }}
                    className="rounded-md"
                  />

                  {/* Holiday legend */}
                  {(closedHolidayDates.length > 0 || limitedHolidayDates.length > 0 || openOverrides.length > 0) && (
                    <div className="mt-3 flex flex-wrap gap-3 text-xs text-zinc-400" data-testid="holiday-legend">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="inline-block w-3 h-3 rounded bg-red-900/60 ring-1 ring-red-500/40"></span>
                        Closed (Holiday)
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="inline-block w-3 h-3 rounded bg-amber-900/60 ring-1 ring-amber-500/40"></span>
                        Limited Hours / Members-Only
                      </span>
                      {openOverrides.length > 0 && (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="inline-block w-3 h-3 rounded bg-lime-900/60 ring-1 ring-lime-500/50"></span>
                          Special Open (normally closed)
                        </span>
                      )}
                    </div>
                  )}

                  {/* Selected holiday banner */}
                  {selectedHoliday && (
                    <div
                      data-testid="selected-holiday-banner"
                      className={`mt-3 p-3 rounded-lg border ${
                        selectedHoliday.closed
                          ? 'bg-red-950/40 border-red-500/40 text-red-200'
                          : 'bg-amber-950/40 border-amber-500/40 text-amber-200'
                      }`}
                    >
                      <p className="font-semibold">
                        {selectedHoliday.closed ? '🔒 ' : '⚠️ '}{selectedHoliday.name}
                      </p>
                      <p className="text-sm mt-1 opacity-90">
                        {selectedHoliday.closed
                          ? "We're closed for this holiday. Please choose another date."
                          : selectedHoliday.members_only
                            ? `Members-only day${selectedHoliday.open_time ? ` — opens ${selectedHoliday.open_time}` : ''}${selectedHoliday.early_close_time ? ` · closes ${selectedHoliday.early_close_time}` : ''}.`
                            : `Limited hours today${selectedHoliday.open_time ? ` — opens ${selectedHoliday.open_time}` : ''}${selectedHoliday.early_close_time ? ` · closes ${selectedHoliday.early_close_time}` : ''}.`}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4">Choose Time</h3>
                {loadingAvailability ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-lime-400 border-t-transparent mx-auto"></div>
                    <p className="text-zinc-400 mt-4">Loading available times...</p>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-center py-8" data-testid="no-slots-message">
                    {(() => {
                      const today = new Date();
                      today.setHours(0,0,0,0);
                      const sel = selectedDate ? new Date(selectedDate) : null;
                      if (sel) sel.setHours(0,0,0,0);
                      const isToday = sel && sel.getTime() === today.getTime();
                      if (availabilityReason) {
                        return (
                          <>
                            <p className="text-amber-400 font-semibold">{availabilityReason}</p>
                            <p className="text-zinc-500 text-sm mt-2">Please choose a different date.</p>
                          </>
                        );
                      }
                      if (isToday) {
                        return (
                          <>
                            <p className="text-zinc-400">No more openings today</p>
                            <p className="text-zinc-500 text-sm mt-2">All remaining slots are booked or have passed. Try tomorrow or another barber.</p>
                          </>
                        );
                      }
                      return (
                        <>
                          <p className="text-zinc-400">No available time slots for this date</p>
                          <p className="text-zinc-500 text-sm mt-2">Please select a different date</p>
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                    {availableSlots.map(time => (
                      <Button
                        key={time}
                        variant={selectedTime === time ? 'default' : 'outline'}
                        className={`${
                          selectedTime === time 
                            ? 'bg-lime-400 text-black hover:bg-lime-500' 
                            : 'border-zinc-700 text-white hover:border-lime-400'
                        }`}
                        onClick={() => setSelectedTime(time)}
                      >
                        {formatTime12Hour(time)}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Recurring Appointment Option - Members Only */}
            {hasMembership && (
              <div className="mt-8 bg-gradient-to-r from-purple-900/20 to-purple-800/10 border-2 border-purple-500/50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="bg-purple-500/20 p-2 rounded-lg mr-3">
                      <RefreshCw className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-white text-lg">Recurring Appointments</h3>
                        <Badge className="bg-lime-500 text-black text-xs">MEMBERS ONLY</Badge>
                      </div>
                      <p className="text-sm text-zinc-400">Never forget to book - schedule regular appointments automatically</p>
                    </div>
                  </div>
                  <Button
                    variant={isRecurring ? 'default' : 'outline'}
                    onClick={() => setIsRecurring(!isRecurring)}
                    className={isRecurring 
                      ? 'bg-lime-400 text-black hover:bg-lime-500' 
                      : 'border-zinc-700 text-white hover:border-lime-400'
                    }
                  >
                    {isRecurring ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Enabled
                      </>
                    ) : (
                      'Enable'
                    )}
                  </Button>
                </div>

              {isRecurring && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-zinc-800">
                  <div>
                    <Label className="text-zinc-400 mb-2 block">Frequency</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <Button
                        variant={recurringFrequency === 'weekly' ? 'default' : 'outline'}
                        onClick={() => setRecurringFrequency('weekly')}
                        className={recurringFrequency === 'weekly'
                          ? 'bg-lime-400 text-black hover:bg-lime-500'
                          : 'border-zinc-700 text-white hover:border-lime-400'
                        }
                      >
                        Weekly
                      </Button>
                      <Button
                        variant={recurringFrequency === 'biweekly' ? 'default' : 'outline'}
                        onClick={() => setRecurringFrequency('biweekly')}
                        className={recurringFrequency === 'biweekly'
                          ? 'bg-lime-400 text-black hover:bg-lime-500'
                          : 'border-zinc-700 text-white hover:border-lime-400'
                        }
                      >
                        Bi-weekly
                      </Button>
                      <Button
                        variant={recurringFrequency === 'monthly' ? 'default' : 'outline'}
                        onClick={() => setRecurringFrequency('monthly')}
                        className={recurringFrequency === 'monthly'
                          ? 'bg-lime-400 text-black hover:bg-lime-500'
                          : 'border-zinc-700 text-white hover:border-lime-400'
                        }
                      >
                        Monthly
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label className="text-zinc-400 mb-2 block">Number of Appointments</Label>
                    <div className="grid grid-cols-4 gap-2">
                      {[4, 6, 8, 12].map(num => (
                        <Button
                          key={num}
                          variant={recurringOccurrences === num ? 'default' : 'outline'}
                          onClick={() => setRecurringOccurrences(num)}
                          className={recurringOccurrences === num
                            ? 'bg-lime-400 text-black hover:bg-lime-500'
                            : 'border-zinc-700 text-white hover:border-lime-400'
                          }
                        >
                          {num}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="md:col-span-2 mt-2">
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <p className="text-sm text-purple-300">
                        <strong>Summary:</strong> You're booking {recurringOccurrences} appointments,{' '}
                        {recurringFrequency === 'weekly' ? 'every week' : 
                         recurringFrequency === 'biweekly' ? 'every 2 weeks' : 'every month'}{' '}
                        on {selectedDate?.toLocaleDateString('en-US', { weekday: 'long' })} at {formatTime12Hour(selectedTime)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Non-Member: Show Recurring Appointments as Locked Feature */}
            {!hasMembership && (
              <div className="mt-8 bg-zinc-900 border-2 border-purple-500/30 rounded-lg p-6 opacity-75">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="bg-zinc-800 p-2 rounded-lg mr-3">
                      <RefreshCw className="w-6 h-6 text-zinc-500" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-zinc-400 text-lg">Recurring Appointments</h3>
                        <Badge className="bg-purple-600 text-lime-400 text-xs">
                          <Lock className="w-3 h-3 mr-1" />
                          MEMBERS ONLY
                        </Badge>
                      </div>
                      <p className="text-sm text-zinc-500">Auto-book weekly, bi-weekly, or monthly appointments</p>
                    </div>
                  </div>
                  <Link to="/membership">
                    <Button className="bg-gradient-to-r from-purple-600 to-purple-700 text-lime-400 hover:from-purple-700 hover:to-purple-800">
                      <Crown className="w-4 h-4 mr-2" />
                      Upgrade
                    </Button>
                  </Link>
                </div>
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3 mt-4">
                  <p className="text-xs text-zinc-400">
                    <Lock className="w-3 h-3 inline mr-1" />
                    Unlock recurring bookings with a membership - never worry about scheduling again!
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Confirm */}
        {step === 4 && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Confirm Your Booking</h2>
            
            {/* DEBUG: Show state values */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mb-4 p-3 bg-blue-900/20 border border-blue-500 rounded text-xs font-mono">
                <p>DEBUG - hasCardOnFile: {String(hasCardOnFile)}</p>
                <p>DEBUG - setupIntentClientSecret: {setupIntentClientSecret ? 'exists' : 'null'}</p>
                <p>DEBUG - currentUser: {currentUser ? 'logged in' : 'not logged in'}</p>
              </div>
            )}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-8">
                <div className="space-y-4">
                  <div>
                    <Label className="text-zinc-400">Service</Label>
                    <p className="text-white text-lg font-semibold">{selectedService.name}</p>
                    <p className="text-lime-400">{selectedService.duration} min</p>
                  </div>
                  <div>
                    <Label className="text-zinc-400">Barber</Label>
                    <p className="text-white text-lg font-semibold">{selectedBarber.name}</p>
                    <p className="text-zinc-500">{selectedBarber.specialty}</p>
                  </div>
                  <div>
                    <Label className="text-zinc-400">Date & Time</Label>
                    <p className="text-white text-lg font-semibold">
                      {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                    <p className="text-zinc-500">{formatTime12Hour(selectedTime)}</p>
                  </div>

                  {isRecurring && (
                    <div className="bg-lime-500/10 border border-lime-500/30 rounded-lg p-4">
                      <Label className="text-lime-400 flex items-center mb-2">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Recurring Appointment
                      </Label>
                      <div className="space-y-1 text-sm">
                        <p className="text-white">
                          <strong>Frequency:</strong> {recurringFrequency === 'weekly' ? 'Every week' : 
                                                       recurringFrequency === 'biweekly' ? 'Every 2 weeks' : 'Monthly'}
                        </p>
                        <p className="text-white">
                          <strong>Total Appointments:</strong> {recurringOccurrences}
                        </p>
                        <p className="text-zinc-400 mt-2">
                          {recurringOccurrences} appointments will be scheduled at {formatTime12Hour(selectedTime)} on {selectedDate?.toLocaleDateString('en-US', { weekday: 'long' })}s
                        </p>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-zinc-400 mb-2 block">Additional Notes (Optional)</Label>
                    <Textarea
                      placeholder="Any special requests or preferences..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>

                  {/* Promo code */}
                  <div>
                    <Label className="text-zinc-400 mb-2 block flex items-center gap-1.5">
                      <Tag className="w-4 h-4" /> Promo code (optional)
                    </Label>
                    {!promoApplied ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input
                            value={promoCode}
                            onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                            placeholder="TAPINTUESDAY"
                            className="bg-zinc-800 border-zinc-700 text-white uppercase tracking-wider"
                            data-testid="promo-code-input"
                          />
                          <Button
                            type="button"
                            onClick={applyPromoCode}
                            disabled={promoChecking || !promoCode.trim()}
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                            data-testid="promo-apply-btn"
                          >
                            {promoChecking ? 'Checking…' : 'Apply'}
                          </Button>
                        </div>
                        {promoError && (
                          <p className="text-xs text-red-400" data-testid="promo-error">{promoError}</p>
                        )}
                      </div>
                    ) : (
                      <div className="bg-lime-500/10 border border-lime-500/30 rounded-lg p-3 flex items-start justify-between gap-2" data-testid="promo-applied">
                        <div className="flex-1 min-w-0">
                          <div className="text-lime-300 font-semibold text-sm">
                            ✓ {promoApplied.title}
                          </div>
                          <div className="text-xs text-zinc-300 mt-0.5">
                            {promoApplied.discount_type === 'percentage'
                              ? `${promoApplied.discount_value}% off applied`
                              : `$${promoApplied.discount_value} off applied`}
                          </div>
                        </div>
                        <button
                          onClick={clearPromoCode}
                          className="text-zinc-400 hover:text-white"
                          aria-label="remove promo"
                          data-testid="promo-clear-btn"
                        >
                          <XIcon className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Email Notification Info */}
            <div className="mt-6 bg-purple-900/20 border border-purple-500/30 p-6 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="bg-purple-500/20 p-2 rounded-lg flex-shrink-0">
                  <span className="text-2xl">📧</span>
                </div>
                <div>
                  <h3 className="font-semibold text-purple-400 mb-1">Email Confirmation</h3>
                  <p className="text-sm text-zinc-300 mb-2">
                    You'll receive an email confirmation with all your booking details.
                  </p>
                  <div className="text-xs text-zinc-500 space-y-1">
                    <p>✓ Instant confirmation email to your inbox</p>
                    <p>✓ Complete booking details and appointment info</p>
                    <p>✓ Email reminder sent 24 hours before appointment</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Card on File Section */}
            {!hasCardOnFile && setupIntentClientSecret && stripePromise && (
              <div className="mt-6 bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                <div className="mb-4">
                  <h3 className="font-semibold text-white mb-2 flex items-center">
                    <Lock className="w-5 h-5 mr-2 text-lime-400" />
                    💳 Card Required for Booking
                  </h3>
                  <div className="space-y-2 text-sm text-zinc-400 mb-4">
                    <p>
                      Please add a valid payment card to complete your booking.
                    </p>
                    <p className="text-xs text-zinc-500">
                      ℹ️ We'll perform a quick $1 verification hold to confirm your card is valid, then immediately release it. No charges will be made unless you miss your appointment per our no-show policy.
                    </p>
                  </div>
                </div>
                <Elements stripe={stripePromise} options={{ clientSecret: setupIntentClientSecret }}>
                  <StripeCardInput 
                    onCardSaved={handleCardSaved} 
                    clientSecret={setupIntentClientSecret} 
                  />
                </Elements>
              </div>
            )}

            {hasCardOnFile && (
              <div className="mt-6 bg-lime-500/10 border border-lime-500/30 rounded-lg p-4 flex items-center space-x-3">
                <Check className="w-6 h-6 text-lime-400 flex-shrink-0" />
                <div>
                  <p className="text-lime-400 font-medium">Card on file ✓</p>
                  <p className="text-sm text-zinc-400">You're all set for booking</p>
                </div>
              </div>
            )}
            
            {/* No-Show Policy */}
            <div className="mt-6 bg-red-900/20 border border-red-500/30 p-6 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="bg-red-500/20 p-2 rounded-lg flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-red-400 mb-2">No-Call / No-Show Policy</h3>
                  <div className="text-sm text-zinc-300 space-y-2">
                    <p>• Appointments must be canceled at least <strong>2 hours in advance</strong></p>
                    <p>• Missed appointments will result in a <strong className="text-red-400">$20 fee</strong></p>
                    <p>• After <strong>3 no-shows</strong>, a <strong className="text-red-400">$20 deposit</strong> will be required for future bookings</p>
                    <p>• Payment requests are sent first, then automatically charged after 24 hours if unpaid</p>
                    <p>• Failed card payments (3 attempts) will block booking access until updated</p>
                  </div>
                  
                  {depositRequired && (
                    <div className="mt-4 bg-purple-900/30 border border-purple-500/50 p-3 rounded">
                      <p className="text-yellow-300 text-sm font-semibold">
                        ⚠️ Deposit Required: Due to previous no-shows, a $20 deposit is required for this booking.
                      </p>
                    </div>
                  )}
                  
                  <div className="mt-4 flex items-start space-x-2">
                    <input
                      type="checkbox"
                      id="policy-agreement"
                      checked={policyAgreed}
                      onChange={(e) => setPolicyAgreed(e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-lime-400 focus:ring-lime-400"
                    />
                    <label htmlFor="policy-agreement" className="text-sm text-zinc-300">
                      I understand and agree to the no-show policy. I acknowledge that missed appointments without proper cancellation will result in a $20 fee.
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            {/* SMS Consent Checkbox - 10DLC Compliance */}
            <div className="mt-6 bg-lime-900/10 border border-lime-500/30 p-6 rounded-lg" data-testid="booking-sms-consent-section">
              <div className="flex items-start space-x-3">
                <div className="bg-lime-500/20 p-2 rounded-lg flex-shrink-0">
                  <span className="text-2xl">📱</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lime-400 mb-3">SMS Text Message Consent</h3>
                  <div className="flex items-start space-x-2">
                    <input
                      type="checkbox"
                      id="sms-consent"
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      required
                      data-testid="booking-sms-consent-checkbox"
                      className="mt-1 w-5 h-5 rounded border-zinc-600 bg-zinc-800 accent-lime-400 flex-shrink-0 cursor-pointer"
                    />
                    <label htmlFor="sms-consent" className="text-sm text-zinc-300 leading-relaxed">
                      I agree to receive SMS appointment confirmations, reminders, and promotional offers
                      from Sauce Sity Barbershop at the phone number on my account. Message frequency varies
                      (typically 2–4 per month). <strong>Message and data rates may apply.</strong> Reply{' '}
                      <strong>STOP</strong> to opt out, reply <strong>HELP</strong> for help. View our{' '}
                      <Link to="/terms-conditions" target="_blank" rel="noopener noreferrer" className="text-lime-400 hover:text-lime-300 underline">
                        Terms
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-lime-400 hover:text-lime-300 underline">
                        Privacy Policy
                      </Link>.
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 bg-zinc-950 p-6 rounded-lg">
              <h3 className="font-semibold mb-2">Important Information:</h3>
              <ul className="text-sm text-zinc-400 space-y-1">
                <li>• Please arrive 5 minutes early</li>
                <li>• Cash payment accepted at the shop</li>
                <li>• Appointment reminder sent 24 hours before</li>
              </ul>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <Button
            onClick={handleBack}
            disabled={step === 1}
            variant="outline"
            className="border-zinc-700 text-white disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          {step < 4 ? (
            <Button
              onClick={handleNext}
              className="bg-gradient-to-r from-lime-500 to-lime-600 text-black font-semibold hover:from-lime-600 hover:to-lime-700"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleBooking}
              disabled={!policyAgreed || !smsConsent || loading || (!hasCardOnFile && !cardSaved)}
              className="bg-gradient-to-r from-lime-500 to-lime-600 text-black font-semibold hover:from-lime-600 hover:to-lime-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Booking...' : 
               (!hasCardOnFile && !cardSaved) ? 'Save Card First' :
               !policyAgreed ? 'Agree to Policy' :
               !smsConsent ? 'SMS Consent Required' :
               'Confirm Booking'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Booking;
